if (!customElements.get("video-banner")) {
  class VideoBanner extends HTMLElement {
    constructor() {
      super();
      this.windowEl = null;
      this.video = null;
      this.playButton = null;
      this.pauseButton = null;
      this.overlay = null;
      this.muteButton = null;
      this.muteIcon = null;
      this.unmuteIcon = null;
      this.progressCircle = null;
      this.progressLength = 0;
      this.isPlaying = false;
      this.isHovering = false;
      this.prevIsPlaying = null;
      this.onPlayClick = this.onPlayClick.bind(this);
      this.onPauseClick = this.onPauseClick.bind(this);
      this.onMouseEnter = this.onMouseEnter.bind(this);
      this.onMouseLeave = this.onMouseLeave.bind(this);
      this.onVideoPlay = this.onVideoPlay.bind(this);
      this.onVideoPause = this.onVideoPause.bind(this);
      this.onMuteClick = this.onMuteClick.bind(this);
      this.onVolumeChange = this.onVolumeChange.bind(this);
      this.onTimeUpdate = this.onTimeUpdate.bind(this);
    }

    connectedCallback() {
      this.windowEl = this.querySelector("[data-video-window]");
      this.video = this.querySelector("[data-video-media] video");
      this.playButton = this.querySelector("[data-video-play]");
      this.pauseButton = this.querySelector("[data-video-pause]");
      this.overlay = this.querySelector("[data-video-overlay]");
      this.muteButton = this.querySelector("[data-mute-button]");
      this.muteIcon = this.querySelector("[data-icon-muted]");
      this.unmuteIcon = this.querySelector("[data-icon-unmuted]");
      this.progressCircle = this.querySelector("[data-video-progress]");
      if (!this.windowEl || !this.video || !this.playButton || !this.pauseButton) {
        return;
      }

      if (this.progressCircle) {
        const radius = parseFloat(this.progressCircle.getAttribute("r")) || 0;
        this.progressLength = 2 * Math.PI * radius;
        this.progressCircle.style.strokeDasharray = `${this.progressLength}`;
        this.progressCircle.style.strokeDashoffset = `${this.progressLength}`;
      }

      this.isPlaying = !this.video.paused;
      this.updateControls();

      this.playButton.addEventListener("click", this.onPlayClick);
      this.pauseButton.addEventListener("click", this.onPauseClick);
      this.muteButton?.addEventListener("click", this.onMuteClick);
      this.windowEl.addEventListener("mouseenter", this.onMouseEnter);
      this.windowEl.addEventListener("mouseleave", this.onMouseLeave);
      this.windowEl.addEventListener("pointerenter", this.onMouseEnter);
      this.windowEl.addEventListener("pointerleave", this.onMouseLeave);
      this.video.addEventListener("play", this.onVideoPlay);
      this.video.addEventListener("pause", this.onVideoPause);
      this.video.addEventListener("volumechange", this.onVolumeChange);
      this.video.addEventListener("timeupdate", this.onTimeUpdate);
      this.updateMuteUi();
    }

    disconnectedCallback() {
      this.playButton?.removeEventListener("click", this.onPlayClick);
      this.pauseButton?.removeEventListener("click", this.onPauseClick);
      this.muteButton?.removeEventListener("click", this.onMuteClick);
      this.windowEl?.removeEventListener("mouseenter", this.onMouseEnter);
      this.windowEl?.removeEventListener("mouseleave", this.onMouseLeave);
      this.windowEl?.removeEventListener("pointerenter", this.onMouseEnter);
      this.windowEl?.removeEventListener("pointerleave", this.onMouseLeave);
      this.video?.removeEventListener("play", this.onVideoPlay);
      this.video?.removeEventListener("pause", this.onVideoPause);
      this.video?.removeEventListener("volumechange", this.onVolumeChange);
      this.video?.removeEventListener("timeupdate", this.onTimeUpdate);
    }

    updateControls() {
      if (!this.playButton || !this.pauseButton) return;
      this.dataset.state = this.isPlaying ? "playing" : "paused";
      const textBlock = this.querySelector("[data-video-text]");
      if (textBlock) {
        textBlock.classList.toggle("opacity-0", this.isPlaying);
        textBlock.classList.toggle("pointer-events-none", this.isPlaying);
        textBlock.classList.toggle("opacity-100", !this.isPlaying);
      }
      this.playButton.classList.toggle("opacity-0", this.isPlaying);
      this.playButton.classList.toggle("pointer-events-none", this.isPlaying);
      this.playButton.classList.toggle("opacity-100", !this.isPlaying);
      this.playButton.classList.toggle("pointer-events-auto", !this.isPlaying);
      this.playButton.setAttribute("tabindex", this.isPlaying ? "-1" : "0");

      const showPause = this.isPlaying && this.isHovering;
      this.pauseButton.classList.toggle("opacity-100", showPause);
      this.pauseButton.classList.toggle("pointer-events-auto", showPause);
      this.pauseButton.classList.toggle("opacity-0", !showPause);
      this.pauseButton.classList.toggle("pointer-events-none", !showPause);
      this.pauseButton.setAttribute("tabindex", this.isPlaying ? "0" : "-1");

      if (this.overlay) {
        this.overlay.classList.toggle("bg-transparent", this.isPlaying);
        this.overlay.classList.toggle(
          "bg-foreground_contrast/30",
          !this.isPlaying
        );
      }
      this.updateMuteUi();

      if (this.prevIsPlaying === true && this.isPlaying === false) {
        this.triggerTextAnimations();
      }
      this.prevIsPlaying = this.isPlaying;
    }

    onPlayClick(event) {
      event.preventDefault();
      this.video?.play().catch(() => {});
      this.isPlaying = true;
      this.updateControls();
    }

    onPauseClick(event) {
      event.preventDefault();
      this.video?.pause();
      this.isPlaying = false;
      this.updateControls();
    }

    onMouseEnter() {
      this.isHovering = true;
      this.updateControls();
    }

    onMouseLeave() {
      this.isHovering = false;
      this.updateControls();
    }

    onVideoPlay() {
      this.isPlaying = true;
      this.updateControls();
    }

    onVideoPause() {
      this.isPlaying = false;
      this.updateControls();
    }

    onTimeUpdate() {
      if (!this.video || !this.progressCircle || !this.progressLength) return;
      const duration = this.video.duration || 0;
      if (!duration || Number.isNaN(duration)) return;
      const progress = this.video.currentTime / duration;
      const offset = this.progressLength * (1 - progress);
      this.progressCircle.style.strokeDashoffset = `${offset}`;
    }

    onMuteClick(event) {
      event.preventDefault();
      if (!this.video) return;
      this.video.muted = !this.video.muted;
      this.updateMuteUi();
    }

    onVolumeChange() {
      this.updateMuteUi();
    }

    updateMuteUi() {
      if (!this.video || !this.muteButton) return;
      const isMuted = this.video.muted;
      this.muteButton.setAttribute("aria-label", isMuted ? "Muted" : "Unmuted");
      this.muteButton.setAttribute("aria-pressed", isMuted ? "true" : "false");
      if (this.muteIcon) {
        this.muteIcon.classList.toggle("hidden", !isMuted);
      }
      if (this.unmuteIcon) {
        this.unmuteIcon.classList.toggle("hidden", isMuted);
      }
    }

    triggerTextAnimations() {
      const animated = this.querySelectorAll("[data-animate-on-toggle]");
      animated.forEach((el) => {
        const animationClass =
          el.getAttribute("data-animate-class") || "animate-fade-in-up";
        el.classList.remove(animationClass);
        el.classList.remove("opacity-100");
        el.classList.add("opacity-0");
        void el.offsetWidth;
        el.classList.add(animationClass);
        const onEnd = () => {
          el.classList.remove("opacity-0");
          el.classList.add("opacity-100");
          el.removeEventListener("animationend", onEnd);
        };
        el.addEventListener("animationend", onEnd);
      });
    }
  }

  customElements.define("video-banner", VideoBanner);
}
